import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/util";
import { Sparkles, Send, Loader2, AlertTriangle, MessageSquare, User, Bot } from "lucide-react";

/**
 * AI Assistant panel — chat interface at the bottom of a finding.
 *
 * Backend contract:
 *   GET  /api/findings/{id}/ai-status   → { available, model, provider }
 *   POST /api/findings/{id}/ai-chat     → { message, history[] }  →  { reply, model, usage }
 *
 * If the server responds ``available=false`` we render the disabled state
 * with the exact copy the user requested.
 */
export function FindingAIChat({ findingId, finding }) {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    setStatusLoading(true);
    api.get(`/findings/${findingId}/ai-status`)
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ available: false, provider: "openai" }))
      .finally(() => setStatusLoading(false));
  }, [findingId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setDraft("");
    try {
      const r = await api.post(`/findings/${findingId}/ai-chat`, {
        message: text,
        history: messages,
      });
      setMessages([...nextMessages, { role: "assistant", content: r.data.reply }]);
    } catch (ex) {
      const detail = ex?.response?.data?.detail || "Error del asistente";
      toast.error(detail);
      // Roll back the user message on hard failure so the input is preserved
      setMessages(messages);
      setDraft(text);
    } finally { setSending(false); }
  }, [draft, messages, findingId, sending]);

  const suggestions = [
    "¿Cómo explotaría un atacante este hallazgo?",
    "Dame un plan de mitigación en 5 pasos concretos.",
    "¿Qué controles ENS / ISO 27001 aplican aquí?",
  ];

  const available = !!status?.available;

  return (
    <section className="border border-primary/40 bg-primary/5 p-5 space-y-3"
             data-testid="finding-ai-panel">
      <div className="flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-primary mt-1" />
        <div className="flex-1">
          <div className="overline text-primary">AI Assistant</div>
          <h2 className="font-display text-lg font-medium">
            Pregúntale al analista IA sobre este hallazgo
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Contexto pre-cargado: <span className="text-foreground">{finding?.title}</span>{" "}
            · severidad <span className="text-foreground">{finding?.severity}</span>
            {finding?.cve && <> · <span className="text-foreground">{finding.cve}</span></>}
          </p>
        </div>
        {status?.model && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {status.provider} · {status.model}
          </span>
        )}
      </div>

      {statusLoading ? (
        <div className="flex items-center gap-2 py-4 text-muted-foreground text-xs">
          <Loader2 className="w-3 h-3 animate-spin" /> Verificando disponibilidad…
        </div>
      ) : !available ? (
        <div className="border border-sev-medium/40 bg-sev-medium/5 p-3 text-xs font-mono flex items-start gap-2"
             data-testid="ai-disabled-notice">
          <AlertTriangle className="w-3 h-3 text-sev-medium flex-shrink-0 mt-0.5" />
          <span className="text-sev-medium">
            Configure OpenAI API key to enable AI Assistant
            <span className="block text-muted-foreground mt-1">
              Añade <code className="text-foreground">OPENAI_API_KEY</code> en{" "}
              <code>backend/.env</code> y reinicia el backend.
            </span>
          </span>
        </div>
      ) : (
        <>
          {messages.length === 0 && (
            <div className="border border-dashed border-primary/30 p-4 space-y-2"
                 data-testid="ai-suggestions">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> Sugerencias
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map(s => (
                  <button key={s}
                          onClick={() => setDraft(s)}
                          className="text-[11px] font-mono border border-border px-2 py-1 hover:border-primary hover:text-primary"
                          data-testid={`ai-suggestion-${s.slice(0,15)}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.length > 0 && (
            <div ref={scrollRef}
                 className="max-h-96 overflow-y-auto space-y-2 pr-1 border border-border/50 p-3 bg-background/40"
                 data-testid="ai-messages">
              {messages.map((m, i) => (
                <div key={i}
                     data-testid={`ai-msg-${m.role}-${i}`}
                     className={cn(
                       "flex gap-2 text-sm",
                       m.role === "user" ? "justify-end" : "justify-start"
                     )}>
                  {m.role === "assistant" && <Bot className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />}
                  <div className={cn(
                    "px-3 py-2 max-w-[85%] whitespace-pre-wrap leading-relaxed",
                    m.role === "user"
                      ? "border border-primary/50 bg-primary/10 text-foreground"
                      : "border border-border bg-card/60"
                  )}>
                    {m.content}
                  </div>
                  {m.role === "user" && <User className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />}
                </div>
              ))}
              {sending && (
                <div className="flex gap-2 items-center text-xs text-muted-foreground">
                  <Bot className="w-4 h-4 text-primary" />
                  <Loader2 className="w-3 h-3 animate-spin" /> Pensando…
                </div>
              )}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex gap-2 items-end">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Escribe tu pregunta — Enter para enviar, Shift+Enter para nueva línea"
              rows={2}
              disabled={sending}
              data-testid="ai-input"
              className="flex-1 bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none resize-none"
            />
            <button type="submit"
                    disabled={sending || !draft.trim()}
                    data-testid="ai-send-btn"
                    className={cn(
                      "px-3 py-2 border transition-colors self-stretch flex items-center gap-1 text-xs font-mono uppercase tracking-widest",
                      sending || !draft.trim()
                        ? "border-border text-muted-foreground cursor-not-allowed"
                        : "border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                    )}>
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Enviar
            </button>
          </form>
        </>
      )}
    </section>
  );
}
